/**
 * Painel Monitoramento — Pré-atendimento.
 * Layout/estilos do mock; dados reais via /api/monitoramento/externo/pre-atendimento.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Headset, Inbox, LayoutList, Sparkles, Ticket, Users } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './MonitoramentoPreAtendimentoDashboard.css';

const POLL_MS = 20000;

const TICKET_STYLE = {
  nova: { label: 'Nova' },
  adiada: { label: 'Adiada' },
  encaminhada: { label: 'Encaminhada' },
  fechada: { label: 'Fechada' },
  excluida: { label: 'Excluída' },
};

const EMPTY_VIEW = {
  pre: {
    name: 'Pré-atendimento',
    color: '#0D9488',
    total: 0,
    pendentes: 0,
    atendimento: 0,
    fila: 0,
    os: { nova: 0, adiada: 0, encaminhada: 0, fechada: 0, excluida: 0 },
    shown: [],
    hasOverflow: false,
    overflowCount: 0,
  },
  totalAbertas: 0,
  totalNovas: 0,
  totalPendentes: 0,
  totalAtendimento: 0,
  totalFila: 0,
  totalNaoLidas: 0,
  totalOsAbertas: 0,
  hasNovasOverdue: false,
  hasNaoLidasOverdue: false,
  osDia: { abertos: 0, encaminhados: 0, fechados: 0 },
  liveText: 'Ao vivo',
  fetchedAt: null,
};

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
}

function ticketStyle(t) {
  const status = TICKET_STYLE[t.status] ? t.status : 'nova';
  const m = TICKET_STYLE[status];
  return {
    number: t.number,
    status,
    statusLabel: m.label,
    isAdiada: status === 'adiada',
    isEncaminhada: status === 'encaminhada',
    isFechada: status === 'fechada',
    isExcluida: status === 'excluida',
  };
}

function normalizeView(raw) {
  if (!raw || typeof raw !== 'object') return EMPTY_VIEW;
  const pre = raw.pre || EMPTY_VIEW.pre;
  const shown = Array.isArray(pre.shown)
    ? pre.shown.map((item) => ({
        ...item,
        tickets: (item.tickets || []).map(ticketStyle),
      }))
    : [];
  return {
    ...EMPTY_VIEW,
    ...raw,
    pre: {
      ...EMPTY_VIEW.pre,
      ...pre,
      os: { ...EMPTY_VIEW.pre.os, ...(pre.os || {}) },
      shown,
    },
    liveText: raw.liveText || 'Ao vivo',
    fetchedAt: raw.fetchedAt || null,
    hasNovasOverdue: Boolean(raw.hasNovasOverdue),
    hasNaoLidasOverdue: Boolean(raw.hasNaoLidasOverdue),
    osDia: {
      abertos: Number(raw.osDia?.abertos) || 0,
      encaminhados: Number(raw.osDia?.encaminhados) || 0,
      fechados: Number(raw.osDia?.fechados) || 0,
    },
  };
}

function formatLiveText(fetchedAt) {
  if (!fetchedAt) return 'Ao vivo';
  const ageSec = Math.max(0, Math.floor((Date.now() - Number(fetchedAt)) / 1000));
  if (ageSec < 5) return 'Ao vivo · agora';
  return `Ao vivo · atualizado há ${ageSec}s`;
}

const WAITING_ALERT_MS = 5 * 60 * 1000;

function isWaitingOverdue(item) {
  if (item?.assignee) return false;
  const ts = Number(item?.lastActivityAt);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  return Date.now() - ts >= WAITING_ALERT_MS;
}

function isUnreadOverdue(item) {
  if (!(item?.hasUnread || (item?.unread || 0) > 0)) return false;
  const ts = Number(item?.lastActivityAt);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  return Date.now() - ts >= WAITING_ALERT_MS;
}

function OsStatusIcon({ ticket }) {
  if (ticket.isExcluida) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );
  }
  if (ticket.isAdiada) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden style={{ flexShrink: 0 }}>
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
    );
  }
  if (ticket.isEncaminhada) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
        <polyline points="9 6 15 12 9 18" />
      </svg>
    );
  }
  if (ticket.isFechada) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  return null;
}

export function MonitoramentoPreAtendimentoDashboard({
  showTitle = false,
  title = 'Pré-atendimento',
  tvCountdown = null,
  tvVisible = true,
}) {
  const [view, setView] = useState(EMPTY_VIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveText, setLiveText] = useState('Ao vivo');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/pre-atendimento`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Erro ${res.status}`);
      }
      const next = normalizeView(json.data);
      setView(next);
      setLiveText(formatLiveText(next.fetchedAt));
      setError(null);
    } catch (e) {
      setError(e.message || 'Erro ao carregar pré-atendimento');
      if (!silent) setView(EMPTY_VIEW);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tvVisible === false) return undefined;
    load({ silent: false });
    const id = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [load, tvVisible]);

  useEffect(() => {
    if (!view.fetchedAt) return undefined;
    const id = setInterval(() => {
      setLiveText(formatLiveText(view.fetchedAt));
    }, 1000);
    return () => clearInterval(id);
  }, [view.fetchedAt]);

  const { pre } = view;
  const novasOverdue = Boolean(
    view.hasNovasOverdue
    || (pre.shown || []).some((item) => item.situacao === 'unattended' && isWaitingOverdue(item))
  );
  const naoLidasOverdue = Boolean(
    view.hasNaoLidasOverdue
    || (pre.shown || []).some((item) => isUnreadOverdue(item))
  );

  return (
    <div className="monitoramento-pre-atendimento">
      {showTitle ? (
        <div className="mpa-tv-title">
          <div className="mpa-tv-title-text">{title}</div>
          {tvCountdown ? (
            <span className="monitoramento-tv-countdown" aria-live="polite" title="Próxima troca de painel">
              {tvCountdown}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mpa-shell">
        {error && !loading ? (
          <div className="mpa-empty-hint" role="alert">{error}</div>
        ) : null}

        <div className="mpa-main-stack">
          <div className="mpa-pre-card">
            <div className="mpa-pre-stat-row">
              <div className="mpa-stat-tile">
                <LayoutList size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value">{view.totalAbertas}</span>
                <span className="mpa-stat-label">Total abertas</span>
              </div>
              <div className={`mpa-stat-tile${novasOverdue ? ' mpa-stat-tile--alert' : ''}`}>
                <Sparkles size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#E11D48' }}>{view.totalNovas}</span>
                <span className="mpa-stat-label">Novas</span>
              </div>
              <div className={`mpa-stat-tile${naoLidasOverdue ? ' mpa-stat-tile--alert' : ''}`}>
                <Inbox size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#0284C7' }}>{view.totalNaoLidas}</span>
                <span className="mpa-stat-label">Não lidas</span>
              </div>
              <div className="mpa-stat-tile">
                <Users size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#2563EB' }}>{view.totalFila}</span>
                <span className="mpa-stat-label">Na fila</span>
              </div>
              <div className="mpa-stat-tile">
                <Clock size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#D97706' }}>{view.totalPendentes}</span>
                <span className="mpa-stat-label">Pendentes</span>
              </div>
              <div className="mpa-stat-tile">
                <Headset size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#6C4CF1' }}>{view.totalAtendimento}</span>
                <span className="mpa-stat-label">Em atendimento</span>
              </div>
              <div className="mpa-stat-tile">
                <Ticket size={16} strokeWidth={2.2} className="mpa-stat-icon" aria-hidden />
                <span className="mpa-stat-value" style={{ color: '#0D9488' }}>{view.totalOsAbertas}</span>
                <span className="mpa-stat-label">OS abertas</span>
              </div>
            </div>

            <div className="mpa-os-summary">
              <span className="mpa-os-summary-label">
                <Ticket size={12} strokeWidth={2} aria-hidden />
                Ordens de serviço
              </span>
              <span className="mpa-os-summary-item mpa-os-summary-item--nova">
                <span className="mpa-os-summary-dot" />
                {pre.os.nova} novas
              </span>
              <span className="mpa-os-summary-item mpa-os-summary-item--adiada">
                <span className="mpa-os-summary-dot" />
                {pre.os.adiada} adiadas
              </span>
              <span className="mpa-os-summary-item mpa-os-summary-item--encaminhada">
                <span className="mpa-os-summary-dot" />
                {pre.os.encaminhada} encaminhadas
              </span>
              <span className="mpa-os-summary-item mpa-os-summary-item--fechada">
                <span className="mpa-os-summary-dot" />
                {pre.os.fechada} fechadas
              </span>
              {(pre.os.excluida || 0) > 0 ? (
                <span className="mpa-os-summary-item mpa-os-summary-item--excluida">
                  <span className="mpa-os-summary-dot" />
                  {pre.os.excluida} excluídas
                </span>
              ) : null}
            </div>

            <div className="mpa-pre-rows">
              {pre.shown.map((item) => (
                <div key={item.id || `${item.contact}-${item.clienteGroup}`} className="mpa-item-row">
                  <span className="mpa-item-client">{item.clienteGroup}</span>
                  <div className="mpa-item-bottom">
                    <div className="mpa-item-avatar" style={{ background: item.avatarColor }}>{item.initials}</div>
                    <span className="mpa-item-contact">{item.contact}</span>
                    {item.pending ? (
                      <span className="mpa-pill mpa-pill--pending">Pendente</span>
                    ) : null}
                    {item.assignee ? (
                      <span className="mpa-pill mpa-pill--atendimento" title={item.assignee.name || undefined}>
                        Em atend.
                      </span>
                    ) : null}
                    <div className="mpa-row-spacer">
                      {item.hasUnread || item.unread > 0 ? (
                        <span
                          className={`mpa-unread-badge${isUnreadOverdue(item) ? ' mpa-unread-badge--overdue' : ''}`}
                        >
                          {item.unread}
                        </span>
                      ) : null}
                      <span
                        className={`mpa-row-time${isWaitingOverdue(item) ? ' mpa-row-time--overdue' : ''}`}
                      >
                        {item.lastActivity}
                      </span>
                    </div>
                  </div>
                  {(item.tickets || []).length > 0 ? (
                    <div className="mpa-os-block">
                      <div className="mpa-os-list">
                        {(item.tickets || []).map((ticket) => (
                          <div
                            key={ticket.number}
                            className={`mpa-os-item mpa-os-item--${ticket.status || 'nova'}`}
                          >
                            <OsStatusIcon ticket={ticket} />
                            <span className="mpa-os-number">OS {ticket.number}</span>
                            <span className="mpa-os-sep">·</span>
                            <span className="mpa-os-status">{ticket.statusLabel}</span>
                          </div>
                        ))}
                      </div>
                      {item.assignee?.name ? (
                        <span className="mpa-os-agent" title={item.assignee.name}>
                          <Headset size={11} strokeWidth={2.2} aria-hidden />
                          <span className="mpa-os-agent-name">{item.assignee.name}</span>
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
              {!loading && !error && pre.shown.length === 0 ? (
                <div className="mpa-overflow-note">Nenhuma conversa em aberto no pré-atendimento</div>
              ) : null}
              {pre.hasOverflow ? (
                <div className="mpa-overflow-note">+{pre.overflowCount} outras conversas</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mpa-summary-bar mpa-summary-bar--live-only">
          <div className="mpa-summary-live">
            <span className="mpa-live-dot" />
            <span className="mpa-summary-live-text">{liveText}</span>
            <span className="mpa-live-sep" aria-hidden>·</span>
            <span className="mpa-live-stat" title="OS abertas hoje">
              <strong className="mpa-live-stat-num">{view.osDia?.abertos ?? 0}</strong>
              <span className="mpa-live-stat-label">abertos</span>
            </span>
            <span className="mpa-live-sep" aria-hidden>·</span>
            <span className="mpa-live-stat mpa-live-stat--enc" title="OS encaminhadas hoje">
              <strong className="mpa-live-stat-num">{view.osDia?.encaminhados ?? 0}</strong>
              <span className="mpa-live-stat-label">enc.</span>
            </span>
            <span className="mpa-live-sep" aria-hidden>·</span>
            <span className="mpa-live-stat mpa-live-stat--fech" title="OS fechadas hoje">
              <strong className="mpa-live-stat-num">{view.osDia?.fechados ?? 0}</strong>
              <span className="mpa-live-stat-label">fech.</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
